import { NgTemplateOutlet } from "@angular/common";
import {
  Component,
  effect,
  ElementRef,
  input,
  signal,
  TemplateRef,
  untracked,
  viewChild,
  viewChildren,
} from "@angular/core";
import { ChipModule } from "primeng/chip";
import { TooltipModule } from "primeng/tooltip";

@Component({
  selector: "app-wrapped-chips-rows",
  standalone: true,
  imports: [ChipModule, TooltipModule, NgTemplateOutlet],
  templateUrl: "./wrapped-chips-rows.component.html",
})
export class WrappedChipsRowsComponent {
  private readonly defaultNumberChipWidth = 64;

  items = input.required<string[]>();
  maxRowCount = input<number>(1);
  rowGapPx = input<number>(8);
  firstItemTemplate = input<TemplateRef<HTMLElement> | null>();
  chipStyleClass = input<string>("");

  container = viewChild<ElementRef>("container");
  numberChip = viewChild<ElementRef>("numberChip");
  firstItem = viewChild<ElementRef>("firstItem");

  chips = viewChildren<ElementRef>("chipItem");

  visibleItems = signal<string[]>([]);
  hiddenItems = signal<string[]>([]);

  constructor() {
    effect(
      () => {
        if (this.items() && this.container()) {
          this.visibleItems.set(this.items()!);
          this.hiddenItems.set([]);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const visibleItems = this.visibleItems();
        if (
          visibleItems.length &&
          visibleItems.length > 1 &&
          visibleItems.length === this.chips().length
        )
          // TODO: Investigate why calculateVisibleItems triggers an infinite loop
          requestAnimationFrame(() => this.calculateVisibleItems(visibleItems));
      },
      {
        allowSignalWrites: true,
      },
    );
  }

  private calculateVisibleItems(items: string[]): void {
    const chips = untracked(this.chips);
    if (!items.length || !chips?.length) {
      return;
    }

    const maxRows = this.maxRowCount();
    const containerWidth = this.container()!.nativeElement.offsetWidth;
    const gap = this.rowGapPx();
    const numberChipWidth =
      untracked(this.numberChip)?.nativeElement.offsetWidth ??
      this.defaultNumberChipWidth;
    const firstItemWidth =
      untracked(this.firstItem)?.nativeElement.offsetWidth ?? 0;

    let totalWidth = firstItemWidth;
    let rowCount = 1;

    for (let index = 0; index < chips.length; index++) {
      const width = chips[index].nativeElement.offsetWidth;
      const newTotalWidth = totalWidth + width + gap;

      if (newTotalWidth > containerWidth && rowCount < maxRows) {
        rowCount++;
        totalWidth = width + gap;
      } else if (newTotalWidth > containerWidth && rowCount === maxRows) {
        const tempIndex =
          totalWidth + numberChipWidth + gap > containerWidth && index !== 1
            ? index - 1
            : index;

        this.setHiddenItems(tempIndex);
        break;
      } else {
        totalWidth += width + gap;
      }
    }
  }

  private setHiddenItems(startIndex: number): void {
    const items = untracked(this.items) ?? [];

    this.visibleItems.set(items.slice(0, startIndex));
    this.hiddenItems.set(items.slice(startIndex));
  }
}
